import { Link } from '../../primitives/Link/Link';
import type { ReactNode } from 'react';
import styles from './SidebarRow.module.css';

/**
 * Shared inner structure of the two Figma row components, `_sidebar item`
 * (48:628) and `_sidebar menu item` (48:2726). Both are:
 *
 *   row (184x30, py 8, radius-md)
 *     content (flex 1, px 8)
 *       text-and-icon (gap 8)
 *         icon wrapper (12x12) | label | counter? | badge?
 *
 * They differ only in their surface tokens and in whether an active state
 * exists, so the markup lives here once and each wrapper supplies its own
 * surface class.
 */
export type SidebarRowProps = {
  label: string;
  /** Instance-swap slot. Pass an `<Icon />`; omit to render the row without one. */
  icon?: ReactNode;
  /** Rendered after the label, matching the Figma layer order. */
  counter?: ReactNode;
  badge?: ReactNode;
  href?: string;
  active?: boolean;
  onClick?: () => void;
  /** Surface class from the owning component. */
  surfaceClassName: string;
  className?: string;
};

export function SidebarRow({
  label,
  icon,
  counter,
  badge,
  href,
  active = false,
  onClick,
  surfaceClassName,
  className,
}: SidebarRowProps) {
  const classes = [styles.row, surfaceClassName, className].filter(Boolean).join(' ');

  const content = (
    <span className={styles.content}>
      <span className={styles.textAndIcon}>
        {icon !== undefined && <span className={styles.iconWrapper}>{icon}</span>}
        <span className={styles.label}>{label}</span>
        {counter}
        {badge}
      </span>
    </span>
  );

  if (href !== undefined) {
    return (
      <Link className={classes} href={href} aria-current={active ? 'page' : undefined} onClick={onClick}>
        {content}
      </Link>
    );
  }

  return (
    <button className={classes} type="button" aria-current={active ? 'page' : undefined} onClick={onClick}>
      {content}
    </button>
  );
}
